import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import {
  EmptyIndicator,
  Pagination,
  TextWithLineBreaks,
  User
} from 'components/common';
import { Box } from 'components/common/box';
import { useAppliedFilterParams } from 'components/common/filter';
import { ReviewStars } from 'components/common/tasks';
import { CalendarEventFeedbackQuestionAnswersSubHeader } from '.';

import type { CalendarEventFeedbackQuestion } from 'shared-types';
import type { GetCalendarEventFeedbackQuestionAnswersRes } from 'shared-types/responses';
import { Icon } from 'components/common/icons';
import { getMessageWithPlaceholders } from 'shared-locales';

interface Props {
  filterId: string;
  filters: Record<string, boolean>;
  numberOfEmptyAnswers: number | null;
  question: CalendarEventFeedbackQuestion;
}

/**
 * Paginated, searchable, filterable list of the submissions that answered
 * `question`. Submissions with no answer for it are not listed at all, so
 * this only ever has as many rows as `numberOfAnswers`.
 *
 * `filterId` and `filters` come from the parent because the `FiltersProvider`
 * feeding `useAppliedFilterParams` has to sit above this component.
 */
const CalendarEventFeedbackQuestionAnswers = ({
  filterId,
  filters,
  numberOfEmptyAnswers,
  question
}: Props) => {
  const {
    messages,
    pageState: { calendarEvent },
    theme
  } = useReduxState();

  const router = useRouter();

  const page = parseInt(router.query.page as string, 10) || 1;
  const search = (router.query.search as string) || '';
  const appliedFilters = useAppliedFilterParams(filterId);
  const filterParams = new URLSearchParams(appliedFilters).toString();

  const { data, loading } =
    useApiData<GetCalendarEventFeedbackQuestionAnswersRes>({
      url:
        `/admin/calendar-events/${calendarEvent.id}/feedback-form/questions/${question.id}` +
        `?page=${page}` +
        (search ? `&search=${encodeURIComponent(search)}` : '') +
        (filterParams ? `&${filterParams}` : '')
    });

  const answers = data?.answers || [];

  const updatePage = (newPage: number) => {
    router.replace({ query: { ...router.query, page: newPage } }, undefined, {
      shallow: true
    });
  };

  return (
    <div className="answers">
      <Box
        loading={loading}
        padding={0}
        subHeaderContent={
          <CalendarEventFeedbackQuestionAnswersSubHeader
            filterId={filterId}
            filters={filters}
          />
        }
        title={
          <div className="title">
            {getMessageWithPlaceholders({
              message: messages.commonNumberOfAnswers,
              placeholders: { numberOfAnswers: data?.numberOfAnswers || 0 }
            })}

            {numberOfEmptyAnswers !== null ? (
              <div className="title-empty-answers">
                {'('}
                {getMessageWithPlaceholders({
                  message:
                    question.type === 'openquestion'
                      ? messages.eventCalendarFeedbackAnswerUnanswered
                      : messages.eventCalendarFeedbackAnswerNoSelections,
                  placeholders: { number: numberOfEmptyAnswers }
                })}
                {')'}
              </div>
            ) : null}
          </div>
        }
      >
        {answers.length ? (
          answers.map((answer) => (
            <div className="answer" key={answer.id}>
              <div className="answer-header">
                <User
                  anonymous={!answer.user}
                  enableProfileLink={true}
                  linkAs={`/calendar/${calendarEvent.id}/feedback/responses/${answer.submissionId}`}
                  linkHref="/calendar/[calendarEventId]/feedback/responses/[responseId]"
                  user={answer.user || undefined}
                />

                <div className="answer-date">
                  {getFormattedDate({ date: answer.createdAt })}
                </div>
              </div>

              <div className="answer-content">
                {question.type === 'openquestion' &&
                answer.answer.type === 'openquestion' ? (
                  <div className="answer-text">
                    <TextWithLineBreaks text={answer.answer.text} />
                  </div>
                ) : null}

                {(question.type === 'singlechoice' ||
                  question.type === 'multiplechoice') &&
                (answer.answer.type === 'singlechoice' ||
                  answer.answer.type === 'multiplechoice') ? (
                  <div className="answer-options">
                    {question.data?.options?.map((option) => {
                      const isSingleChoice = question.type === 'singlechoice';
                      const isSelected =
                        (answer.answer.type === 'singlechoice' ||
                          answer.answer.type === 'multiplechoice') &&
                        answer.answer.optionIds?.includes(option.id);

                      return (
                        <div
                          className={`answer-option ${
                            isSingleChoice ? 'answer-option-singlechoice' : ''
                          } ${isSelected ? 'answer-option-selected' : ''}`}
                          key={option.id}
                        >
                          <div className="answer-option-icon">
                            {isSelected ? <Icon icon="check" /> : null}
                          </div>

                          <div className="answer-option-text">
                            {option.text}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {question.type === 'starrating' &&
                answer.answer.type === 'starrating' ? (
                  <div className="answer-rating">
                    <ReviewStars
                      numberOfStars={question.data.numberOfStars}
                      value={answer.answer.rating}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ))
        ) : (
          <EmptyIndicator
            icon={['far', 'file-alt']}
            title={messages.tasksNoAnswers}
          />
        )}
      </Box>

      {answers.length ? (
        <Pagination
          currentPage={page}
          numberOfPages={data?.pages || 1}
          onPageChange={({ page: newPage }) => updatePage(newPage)}
        />
      ) : null}

      <style jsx>{`
        .title {
          align-items: center;
          display: flex;
          gap: 20px;
        }
        .title-empty-answers {
          color: ${theme.textColorLighter};
          font-size: 1.5rem;
          font-weight: 400;
        }
        .answer {
          padding: 20px;
        }
        .answer:not(:first-child) {
          border-top: 1px solid ${theme.lightGrey};
        }
        .answer-header {
          align-items: center;
          display: flex;
          gap: 15px;
          justify-content: space-between;
        }
        .answer-date {
          color: ${theme.textColorLighter};
          font-size: 1.4rem;
          font-weight: 600;
        }
        .answer-content {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
          margin-top: 15px;
          max-width: max-content;
          min-width: 120px;
          padding: 10px 15px;
        }
        .answer-text {
          color: ${theme.textColor};
          font-size: 1.5rem;
        }
        .answer-options {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .answer-option {
          display: flex;
          gap: 8px;
        }
        .answer-option-icon {
          align-items: center;
          background-color: ${theme.lightGreyDark};
          border: 0;
          border-radius: 5px;
          display: inline-flex;
          flex-shrink: 0;
          font-size: 0.8rem;
          font-weight: 400;
          height: 16px;
          justify-content: center;
          position: relative;
          top: 2px;
          width: 16px;
        }
        .answer-option-singlechoice .answer-option-icon {
          border-radius: 50px;
        }
        .answer-option-selected .answer-option-icon {
          background-color: ${theme.successColor};
          color: ${theme.successTextColor};
        }
        .answer-option-text {
          color: ${theme.textColorLighter};
          font-size: 1.5rem;
        }
        .answer-option-selected .answer-option-text {
          color: ${theme.textColor};
        }
        .answer-rating {
          color: ${theme.textColorLight};
          font-size: 1.3rem;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventFeedbackQuestionAnswers;
